// =============================================================================
// VandieZ admin-web -- Settings > SMS (/settings/sms)
// =============================================================================
// Shows today's SMS cost dashboard + per-event config table.
// Mirrors the DisputesPage idiom: useState + useEffect, no react-query.
// =============================================================================

import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  IndianRupee,
  Loader2,
  MessageSquare,
  Save,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { smsAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  SmsCostDashboard,
  SmsDeliveryMode,
  SmsEventConfig,
  SmsLanguageMode,
} from "@/types/sms";

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function fmtInr(val: string): string {
  const n = Number.parseFloat(val);
  return Number.isNaN(n) ? val : `₹${n.toFixed(2)}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

const DELIVERY_OPTIONS: { label: string; value: SmsDeliveryMode }[] = [
  { label: "Parallel", value: "parallel" },
  { label: "FCM first", value: "fcm_first" },
  { label: "SMS only", value: "sms_only" },
];

const LANGUAGE_OPTIONS: { label: string; value: SmsLanguageMode }[] = [
  { label: "Bilingual", value: "bilingual" },
  { label: "Per user", value: "per_user" },
  { label: "English only", value: "english_only" },
];

// ---------------------------------------------------------------------------
// Sub-component: inline template editor
// ---------------------------------------------------------------------------

interface TemplateEditorProps {
  config: SmsEventConfig;
  onSaved: (updated: SmsEventConfig) => void;
  accessToken: string;
}

function TemplateEditor({ config, onSaved, accessToken }: TemplateEditorProps): React.JSX.Element {
  const [templateEn, setTemplateEn] = useState(config.template_en);
  const [templateTa, setTemplateTa] = useState(config.template_ta ?? "");
  const [dltId, setDltId] = useState(config.dlt_template_id ?? "");
  const [budget, setBudget] = useState(config.daily_budget_inr);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function handleSave(): Promise<void> {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await smsAdminApi.update(accessToken, config.event_key, {
        template_en: templateEn,
        template_ta: templateTa.trim() === "" ? null : templateTa,
        dlt_template_id: dltId.trim() === "" ? null : dltId,
        daily_budget_inr: budget,
      });
      onSaved(updated);
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.problem.detail : "Save failed.";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-3 rounded border bg-muted/20 p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Templates &amp; budget — {config.event_key}
      </p>
      {saveError !== null && (
        <Alert variant="destructive" className="mb-3">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{saveError}</AlertDescription>
        </Alert>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label htmlFor={`en-${config.event_key}`} className="text-xs">
            English template
          </Label>
          <Textarea
            id={`en-${config.event_key}`}
            rows={3}
            value={templateEn}
            onChange={(e) => { setTemplateEn(e.target.value); }}
            className="mt-1 font-mono text-xs"
          />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor={`ta-${config.event_key}`} className="text-xs">
            Tamil template (optional)
          </Label>
          <Textarea
            id={`ta-${config.event_key}`}
            rows={3}
            value={templateTa}
            onChange={(e) => { setTemplateTa(e.target.value); }}
            className="mt-1 font-mono text-xs"
          />
        </div>
        <div>
          <Label htmlFor={`dlt-${config.event_key}`} className="text-xs">
            DLT template ID
          </Label>
          <Input
            id={`dlt-${config.event_key}`}
            value={dltId}
            onChange={(e) => { setDltId(e.target.value); }}
            placeholder="optional"
            className="mt-1 text-xs"
          />
        </div>
        <div>
          <Label htmlFor={`budget-${config.event_key}`} className="text-xs">
            Daily budget (INR)
          </Label>
          <Input
            id={`budget-${config.event_key}`}
            value={budget}
            onChange={(e) => { setBudget(e.target.value); }}
            placeholder="0.00"
            className="mt-1 text-xs"
          />
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" disabled={saving} onClick={() => void handleSave()}>
          {saving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Save className="mr-1 h-3 w-3" />}
          Save
        </Button>
        <span className="text-xs text-muted-foreground">
          Last updated: {formatDate(config.updated_at)}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function SmsSettingsPage(): React.JSX.Element {
  const { accessToken } = useAuth();

  const [configs, setConfigs] = useState<SmsEventConfig[]>([]);
  const [costs, setCosts] = useState<SmsCostDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // cap editing
  const [editingCap, setEditingCap] = useState(false);
  const [newCap, setNewCap] = useState("");
  const [capSaving, setCapSaving] = useState(false);
  const [capError, setCapError] = useState<string | null>(null);

  // which row has template editor open
  const [openEditor, setOpenEditor] = useState<string | null>(null);

  // row-level update errors
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) { return; }
      setLoading(true);
      setError(null);
      try {
        const [cfgs, costData] = await Promise.all([
          smsAdminApi.list(accessToken, signal),
          smsAdminApi.costs(accessToken, signal),
        ]);
        setConfigs(cfgs);
        setCosts(costData);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") { return; }
        const msg = e instanceof ApiError ? e.problem.detail : "Unexpected error.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => { controller.abort(); };
  }, [load]);

  async function patchConfig(
    eventKey: string,
    patch: Partial<Pick<SmsEventConfig, "enabled" | "delivery_mode" | "language_mode">>,
  ): Promise<void> {
    if (accessToken === null) { return; }
    setRowErrors((prev) => ({ ...prev, [eventKey]: "" }));
    try {
      const updated = await smsAdminApi.update(accessToken, eventKey, patch);
      setConfigs((prev) => prev.map((c) => (c.event_key === eventKey ? updated : c)));
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.problem.detail : "Update failed.";
      setRowErrors((prev) => ({ ...prev, [eventKey]: msg }));
    }
  }

  async function saveCap(): Promise<void> {
    if (accessToken === null) { return; }
    setCapSaving(true);
    setCapError(null);
    try {
      const updated = await smsAdminApi.updateCap(accessToken, newCap);
      setCosts(updated);
      setEditingCap(false);
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.problem.detail : "Failed to update cap.";
      setCapError(msg);
    } finally {
      setCapSaving(false);
    }
  }

  const spentPct =
    costs !== null
      ? Math.min(
          100,
          (Number.parseFloat(costs.spent_today_inr) /
            Math.max(0.01, Number.parseFloat(costs.daily_cap_inr))) *
            100,
        )
      : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings › SMS"
        description="Event configuration, daily budget cap, and per-event template editor."
        actions={
          <Link to="/settings/sms/log" className="text-primary hover:underline text-sm">
            View dispatch log →
          </Link>
        }
      />
        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* Today's spend card                                                  */}
        {/* ------------------------------------------------------------------ */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <IndianRupee className="h-5 w-5" /> Today&apos;s SMS spend
            </CardTitle>
            <CardDescription>
              Running total vs. daily cap. Resets at midnight IST.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading && costs === null ? (
              <div className="flex items-center gap-2 py-4 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : costs !== null ? (
              <div className="space-y-4">
                <div className="flex items-end gap-4">
                  <div>
                    <p className="text-3xl font-bold tabular-nums">
                      {fmtInr(costs.spent_today_inr)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      of {fmtInr(costs.daily_cap_inr)} cap
                    </p>
                  </div>
                  <div className="flex-1">
                    <div className="h-3 overflow-hidden rounded-full bg-muted">
                      <div
                        className={cn(
                          "h-full rounded-full transition-all",
                          spentPct >= 90 ? "bg-danger" : spentPct >= 70 ? "bg-warning" : "bg-success",
                        )}
                        style={{ width: `${spentPct}%` }}
                      />
                    </div>
                    <p className="mt-1 text-right text-xs text-muted-foreground">
                      {spentPct.toFixed(1)}%
                    </p>
                  </div>
                </div>

                {editingCap ? (
                  <div className="flex items-center gap-2">
                    {capError !== null && (
                      <span className="text-xs text-destructive">{capError}</span>
                    )}
                    <Label htmlFor="cap-input" className="text-sm">New cap (INR):</Label>
                    <Input
                      id="cap-input"
                      value={newCap}
                      onChange={(e) => { setNewCap(e.target.value); }}
                      placeholder="e.g. 500.00"
                      className="w-32 text-sm"
                    />
                    <Button size="sm" disabled={capSaving} onClick={() => void saveCap()}>
                      {capSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setEditingCap(false); setCapError(null); }}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setNewCap(costs.daily_cap_inr);
                      setEditingCap(true);
                    }}
                  >
                    Update cap
                  </Button>
                )}
              </div>
            ) : null}
          </CardContent>
        </Card>

        {/* ------------------------------------------------------------------ */}
        {/* Per-event cost mini-cards                                           */}
        {/* ------------------------------------------------------------------ */}
        {costs !== null && costs.per_event.length > 0 && (
          <div>
            <p className="mb-2 text-sm font-medium text-muted-foreground">Per-event spend today</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {costs.per_event.map((ev) => (
                <Card key={ev.event_key} className="relative">
                  {ev.failed_today > 0 && (
                    <span className="absolute right-2 top-2 rounded-full bg-destructive px-2 py-0.5 text-xs text-destructive-foreground">
                      {ev.failed_today} failed
                    </span>
                  )}
                  <CardHeader className="pb-1 pt-3">
                    <CardTitle className="text-sm font-mono">
                      {ev.event_key.replace(/_/g, " ")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pb-3">
                    <p className="text-lg font-semibold tabular-nums">
                      {fmtInr(ev.spent_today_inr)}
                    </p>
                    <p className="text-xs text-muted-foreground">{ev.sent_today} sent</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* Event config table                                                  */}
        {/* ------------------------------------------------------------------ */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" /> Event configuration
            </CardTitle>
            <CardDescription>
              Toggle, delivery mode, and language per event. Click &quot;Edit templates&quot; to update message content.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading && configs.length === 0 ? (
              <div className="flex items-center gap-2 py-8 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : configs.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">No event configs found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-4">Event</th>
                      <th className="py-2 pr-4">Enabled</th>
                      <th className="py-2 pr-4">Delivery</th>
                      <th className="py-2 pr-4">Language</th>
                      <th className="py-2 pr-4">Budget/day</th>
                      <th className="py-2 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {configs.map((cfg) => (
                      <>
                        <tr
                          key={cfg.event_key}
                          className="border-b last:border-b-0 hover:bg-muted/30"
                        >
                          <td className="py-3 pr-4 font-mono text-xs">
                            {cfg.event_key}
                          </td>
                          <td className="py-3 pr-4">
                            <button
                              onClick={() => void patchConfig(cfg.event_key, { enabled: !cfg.enabled })}
                              className={cn(
                                "rounded-full px-2 py-0.5 text-xs font-medium transition-colors",
                                cfg.enabled
                                  ? "bg-success-subtle text-success-subtle-foreground hover:bg-success-subtle/80"
                                  : "bg-muted text-muted-foreground hover:bg-muted/80",
                              )}
                            >
                              {cfg.enabled ? "On" : "Off"}
                            </button>
                          </td>
                          <td className="py-3 pr-4">
                            <select
                              value={cfg.delivery_mode}
                              onChange={(e) =>
                                void patchConfig(cfg.event_key, {
                                  delivery_mode: e.target.value as SmsDeliveryMode,
                                })
                              }
                              className="rounded border bg-background px-2 py-1 text-xs"
                            >
                              {DELIVERY_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="py-3 pr-4">
                            <select
                              value={cfg.language_mode}
                              onChange={(e) =>
                                void patchConfig(cfg.event_key, {
                                  language_mode: e.target.value as SmsLanguageMode,
                                })
                              }
                              className="rounded border bg-background px-2 py-1 text-xs"
                            >
                              {LANGUAGE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="py-3 pr-4 tabular-nums">
                            {fmtInr(cfg.daily_budget_inr)}/day
                          </td>
                          <td className="py-3 pr-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setOpenEditor((prev) =>
                                  prev === cfg.event_key ? null : cfg.event_key,
                                );
                              }}
                            >
                              {openEditor === cfg.event_key ? (
                                <>
                                  <ChevronUp className="mr-1 h-3 w-3" /> Close
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="mr-1 h-3 w-3" /> Edit templates
                                </>
                              )}
                            </Button>
                          </td>
                        </tr>
                        {rowErrors[cfg.event_key] ? (
                          <tr key={`${cfg.event_key}-err`}>
                            <td colSpan={6} className="pb-2 pt-0">
                              <span className="text-xs text-destructive">
                                {rowErrors[cfg.event_key]}
                              </span>
                            </td>
                          </tr>
                        ) : null}
                        {openEditor === cfg.event_key && accessToken !== null ? (
                          <tr key={`${cfg.event_key}-editor`}>
                            <td colSpan={6} className="pb-3 pt-0">
                              <TemplateEditor
                                config={cfg}
                                accessToken={accessToken}
                                onSaved={(updated) => {
                                  setConfigs((prev) =>
                                    prev.map((c) =>
                                      c.event_key === updated.event_key ? updated : c,
                                    ),
                                  );
                                  setOpenEditor(null);
                                }}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
    </div>
  );
}
